import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/components/Auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, Circle, User, Settings, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { PlatformTour } from './PlatformTour';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  action: () => void;
  path?: string;
}

interface UserOnboardingProps {
  onComplete: () => void;
  onSkip: () => void;
}

export const UserOnboarding = ({ onComplete, onSkip }: UserOnboardingProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Add state to control modal visibility
  const [visible, setVisible] = useState(true);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (user) {
      checkOnboardingStatus();
    }
  }, [user]);

  const checkOnboardingStatus = async () => {
    if (!user) return;

    try {
      // Check user profile from Supabase
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
      } else {
        setUserProfile(profile);
      }

    } catch (error) {
      console.error('Error checking onboarding status:', error);
      toast({
        title: "Error",
        description: "Failed to load onboarding status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Simplified step order: profile -> tour (removed storage step)
  const steps: OnboardingStep[] = [
    {
      id: 'profile',
      title: 'Complete Your Profile',
      description: 'Add your personal information, organization details, and ESG commitments.',
      icon: <User className="h-5 w-5" />,
      completed: userProfile?.full_name && userProfile?.job_title && userProfile?.company_name,
      action: () => {
        setVisible(false);
        navigate('/settings?tab=profile');
      },
      path: '/settings?tab=profile'
    },
    {
      id: 'welcome-tour',
      title: 'Quick Platform Tour',
      description: 'Learn about key features and navigation. You can skip this step.',
      icon: <Settings className="h-5 w-5" />,
      completed: localStorage.getItem('welcome_tour_completed') === 'true',
      action: () => {
        setShowTour(true);
      }
    }
  ];

  // Determine current step (first incomplete)
  const currentStepIndex = steps.findIndex(step => !step.completed);
  const currentStep = steps[currentStepIndex];
  const allCompleted = currentStepIndex === -1;
  const progressPercentage = ((steps.length - (allCompleted ? 0 : steps.length - currentStepIndex)) / steps.length) * 100;

  // When onboarding status changes (e.g., after profile setup), re-check and show modal if needed
  useEffect(() => {
    if (!loading && !allCompleted && !visible) {
      setVisible(true);
    }
  }, [userProfile, loading, allCompleted]);

  if (loading || !visible) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Welcome to GreenCard Sustainability Manager</CardTitle>
          <p className="text-muted-foreground mt-2">
            Let's get you set up to start your ESG journey
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Setup Progress</span>
              <span className="text-sm text-muted-foreground">
                {steps.filter(s => s.completed).length} of {steps.length} completed
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>

          <div className="space-y-3">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center p-4 rounded-lg border transition-colors ${
                  step.completed 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-card border-border hover:bg-accent/50'
                } ${index > currentStepIndex ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <div className="flex-shrink-0 mr-4">
                  {step.completed ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <Circle className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {step.icon}
                    <h3 className="font-medium">{step.title}</h3>
                    {step.completed && (
                      <Badge variant="secondary" className="text-xs">
                        ✓ Complete
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {step.description}
                  </p>
                </div>
                {!step.completed && index === currentStepIndex && (
                  <Button 
                    onClick={step.action}
                    variant="outline" 
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    {step.id === 'welcome-tour' ? 'Start Tour' : 'Setup'}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
                {step.id === 'welcome-tour' && !step.completed && index === currentStepIndex && (
                  <Button 
                    onClick={onSkip}
                    variant="ghost"
                    size="sm"
                    className="ml-2"
                  >
                    Skip Tour
                  </Button>
                )}
              </div>
            ))}
          </div>

          {allCompleted && (
            <div className="text-center space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <h3 className="font-medium text-green-800">Setup Complete!</h3>
                <p className="text-sm text-green-600">
                  You're ready to start tracking your ESG performance
                </p>
              </div>
              <Button onClick={onComplete} className="w-full">
                Start Using GreenCard
              </Button>
            </div>
          )}

          {showTour && currentStep?.id === 'welcome-tour' && (
            <PlatformTour
              onFinish={() => {
                localStorage.setItem('welcome_tour_completed', 'true');
                setShowTour(false);
                onComplete();
              }}
              onSkip={() => {
                localStorage.setItem('welcome_tour_completed', 'true');
                setShowTour(false);
                onSkip();
              }}
              role={userProfile?.role || 'data_entry'}
            />
          )}

          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              You can always complete these steps later in Settings
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};







// import React, { useState, useEffect } from 'react';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Button } from '@/components/ui/button';
// import { Progress } from '@/components/ui/progress';
// import { Badge } from '@/components/ui/badge';
// import { useAuth } from '@/components/Auth/AuthProvider';
// import { supabase } from '@/integrations/supabase/client';
// import { validateStorageConfiguration } from '@/utils/storageValidation';
// import { CheckCircle, Circle, User, HardDrive, Settings, ChevronRight, X } from 'lucide-react';
// import { useNavigate } from 'react-router-dom';
// import { useToast } from '@/hooks/use-toast';
// import { PlatformTour } from './PlatformTour';

// interface OnboardingStep {
//   id: string;
//   title: string;
//   description: string;
//   icon: React.ReactNode;
//   completed: boolean;
//   action: () => void;
//   path?: string;
// }

// interface UserOnboardingProps {
//   onComplete: () => void;
//   onSkip: () => void;
// }

// export const UserOnboarding = ({ onComplete, onSkip }: UserOnboardingProps) => {
//   const { user } = useAuth();
//   const navigate = useNavigate();
//   const { toast } = useToast();
//   const [userProfile, setUserProfile] = useState<any>(null);
//   const [storageConfigured, setStorageConfigured] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [storageValidation, setStorageValidation] = useState<any>(null);
//   // Add state to control modal visibility
//   const [visible, setVisible] = useState(true);
//   const [showTour, setShowTour] = useState(false);

//   useEffect(() => {
//     if (user) {
//       checkOnboardingStatus();
//     }
//   }, [user]);

//   const checkOnboardingStatus = async () => {
//     if (!user) return;

//     try {
//       // Check user profile
//       const { data: profile } = await supabase
//         .from('user_profiles')
//         .select('*')
//         .eq('user_id', user.id)
//         .single();

//       setUserProfile(profile);

//       // Check storage configuration with detailed validation
//       const validation = await validateStorageConfiguration();
//       setStorageValidation(validation);
//       setStorageConfigured(validation.isConfigured);

//     } catch (error) {
//       console.error('Error checking onboarding status:', error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Strict step order: storage -> profile -> tour
//   const steps: OnboardingStep[] = [
//     {
//       id: 'storage',
//       title: 'Connect Local Storage',
//       description: 'Select a folder on your device where all ESG data will be stored. This is required before using the platform.',
//       icon: <HardDrive className="h-5 w-5" />,
//       completed: storageConfigured,
//       action: () => {
//         setVisible(false);
//         navigate('/settings?tab=storage');
//       },
//       path: '/settings?tab=storage'
//     },
//     {
//       id: 'profile',
//       title: 'Complete Your Profile',
//       description: 'Add your personal information and contact details. Required before using the platform.',
//       icon: <User className="h-5 w-5" />,
//       completed: userProfile?.full_name && userProfile?.job_title && userProfile?.company_name,
//       action: () => {
//         setVisible(false);
//         navigate('/settings?tab=profile');
//       },
//       path: '/settings?tab=profile'
//     },
//     {
//       id: 'welcome-tour',
//       title: 'Quick Platform Tour',
//       description: 'Learn about key features and navigation. You can skip this step.',
//       icon: <Settings className="h-5 w-5" />,
//       completed: localStorage.getItem('welcome_tour_completed') === 'true',
//       action: () => {
//         setShowTour(true);
//       }
//     }
//   ];

//   // Determine current step (first incomplete)
//   const currentStepIndex = steps.findIndex(step => !step.completed);
//   const currentStep = steps[currentStepIndex];
//   const allCompleted = currentStepIndex === -1;
//   const progressPercentage = ((steps.length - (allCompleted ? 0 : steps.length - currentStepIndex)) / steps.length) * 100;

//   // When onboarding status changes (e.g., after storage setup), re-check and show modal if needed
//   useEffect(() => {
//     if (!loading && !allCompleted && !visible) {
//       setVisible(true);
//     }
//   }, [storageConfigured, userProfile, loading, allCompleted]);

//   if (loading || !visible) {
//     return null;
//   }

//   return (
//     <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
//       <Card className="w-full max-w-2xl">
//         <CardHeader className="text-center">
//           <CardTitle className="text-2xl">Welcome to GreenCard Sustainability Manager</CardTitle>
//           <p className="text-muted-foreground mt-2">
//             Let's get you set up to start your ESG journey
//           </p>
//         </CardHeader>
//         <CardContent className="space-y-6">
//           <div className="space-y-2">
//             <div className="flex items-center justify-between">
//               <span className="text-sm font-medium">Setup Progress</span>
//               <span className="text-sm text-muted-foreground">
//                 {steps.filter(s => s.completed).length} of {steps.length} completed
//               </span>
//             </div>
//             <Progress value={progressPercentage} className="h-2" />
//           </div>

//           <div className="space-y-3">
//             {steps.map((step, index) => (
//               <div
//                 key={step.id}
//                 className={`flex items-center p-4 rounded-lg border transition-colors ${
//                   step.completed 
//                     ? 'bg-green-50 border-green-200' 
//                     : 'bg-card border-border hover:bg-accent/50'
//                 } ${index > currentStepIndex ? 'opacity-50 pointer-events-none' : ''}`}
//               >
//                 <div className="flex-shrink-0 mr-4">
//                   {step.completed ? (
//                     <CheckCircle className="h-6 w-6 text-green-600" />
//                   ) : (
//                     <Circle className="h-6 w-6 text-muted-foreground" />
//                   )}
//                 </div>
//                 <div className="flex-1">
//                   <div className="flex items-center gap-2">
//                     {step.icon}
//                     <h3 className="font-medium">{step.title}</h3>
//                     {step.completed && (
//                       <Badge variant="secondary" className="text-xs">
//                         ✓ Complete
//                       </Badge>
//                     )}
//                   </div>
//                   <p className="text-sm text-muted-foreground mt-1">
//                     {step.description}
//                   </p>
//                 </div>
//                 {!step.completed && index === currentStepIndex && (
//                   <Button 
//                     onClick={step.action}
//                     variant="outline" 
//                     size="sm"
//                     className="flex items-center gap-2"
//                   >
//                     {step.id === 'welcome-tour' ? 'Start Tour' : 'Setup'}
//                     <ChevronRight className="h-4 w-4" />
//                   </Button>
//                 )}
//                 {step.id === 'welcome-tour' && !step.completed && index === currentStepIndex && (
//                   <Button 
//                     onClick={onSkip}
//                     variant="ghost"
//                     size="sm"
//                     className="ml-2"
//                   >
//                     Skip Tour
//                   </Button>
//                 )}
//               </div>
//             ))}
//           </div>

//           {allCompleted && (
//             <div className="text-center space-y-4">
//               <div className="p-4 bg-green-50 rounded-lg border border-green-200">
//                 <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
//                 <h3 className="font-medium text-green-800">Setup Complete!</h3>
//                 <p className="text-sm text-green-600">
//                   You're ready to start tracking your ESG performance
//                 </p>
//               </div>
//               <Button onClick={onComplete} className="w-full">
//                 Start Using GreenCard
//               </Button>
//             </div>
//           )}

//           {showTour && currentStep.id === 'welcome-tour' && (
//             <PlatformTour
//               onFinish={() => {
//                 localStorage.setItem('welcome_tour_completed', 'true');
//                 setShowTour(false);
//                 onComplete();
//               }}
//               onSkip={() => {
//                 localStorage.setItem('welcome_tour_completed', 'true');
//                 setShowTour(false);
//                 onSkip();
//               }}
//               role={userProfile?.role || 'data_entry'}
//             />
//           )}

//           <div className="text-center">
//             <p className="text-xs text-muted-foreground">
//               You can always complete these steps later in Settings
//             </p>
//           </div>
//         </CardContent>
//       </Card>
//     </div>
//   );
// };
